import React, { createRef } from 'react';
import {
    GridComponent,
    ColumnsDirective,
    ColumnDirective,
    Inject,
    Sort,
    Filter,
    Page,
    Group,
    Resize,
    CommandColumn,
    RowDataBoundEventArgs,
    RowSelectEventArgs,
    SortSettingsModel,
    FilterSettingsModel,
    GroupSettingsModel,
    PageSettingsModel,
    CommandModel,
    Column,
} from '@syncfusion/ej2-react-grids';
import DisclosureWidget from './DisclosureWidget';

declare var InovxLoader: any;

interface GridProps {
    filter?: boolean;
    columns: any[];
    dataSource?: any;
    selectRow?: any;
    commands?: any;
    reset?: boolean;
    sort?: boolean;
    hideHeader?: boolean;
    pageable?: any;
    defaultSort?: any[];
    scrollHeight?: number;
    groupBy?: string;
    applyRowClass?: (data: any) => string;
    clickableLinks?: boolean;
    allowScrolling?: boolean;
    autoSelectSingleRow?: boolean;
    rowCommandsHandler?: (args: any) => void;
}

interface GridState {
    rendered: boolean;
}

class Grid extends React.Component<GridProps, GridState> {
    private gridRef = createRef<GridComponent>();

    constructor(props: GridProps) {
        super(props);
        this.state = { rendered: false };
    }

    private _isUrlValid(userInput: string) {
        var res = userInput.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g);
        return res != null;
    }

    private _buildColumns(): any[] {
        const cols = [...this.props.columns];

        // Add command column if commands prop is provided
        if (this.props.commands) {
            const commandModels: CommandModel[] = this.props.commands.command.map((cmd: any, idx: number) => {
                const cmdModel: any = {
                    type: 'None',
                    buttonOption: {} as any,
                };
                if (cmd.text) {
                    cmdModel.buttonOption.content = cmd.text;
                }
                if (cmd.iconClassName) {
                    cmdModel.buttonOption.iconCss = cmd.iconClassName;
                }
                if (cmd.className) {
                    cmdModel.buttonOption.cssClass = cmd.className;
                }
                return cmdModel;
            });

            cols.push({
                headerText: this.props.commands.headerText,
                width: this.props.commands.width,
                _isCommandColumn: true,
                _commands: commandModels,
                _rawCommands: this.props.commands.command,
            });
        }

        return cols;
    }

    private _getSortSettings(): SortSettingsModel | undefined {
        if (!this.props.sort) return undefined;
        if (this.props.defaultSort) {
            return {
                columns: this.props.defaultSort.map(s => ({
                    field: s.field,
                    direction: s.dir || 'Ascending',
                })),
            };
        }
        return {};
    }

    private _getFilterSettings(): FilterSettingsModel | undefined {
        if (!this.props.filter) return undefined;
        return { type: 'Menu' };
    }

    private _getPageSettings(): PageSettingsModel | undefined {
        if (!this.props.pageable) return undefined;
        const settings: PageSettingsModel = {};
        if (typeof this.props.pageable === 'object') {
            if (this.props.pageable.pageCount) settings.pageCount = this.props.pageable.pageCount;
            if (this.props.pageable.pageSize) settings.pageSize = this.props.pageable.pageSize;
            if (this.props.pageable.pageSizes) settings.pageSizes = this.props.pageable.pageSizes;
        }
        return settings;
    }

    private _getGroupSettings(): GroupSettingsModel | undefined {
        if (!this.props.groupBy) return undefined;
        return {
            columns: [this.props.groupBy],
            showDropArea: false,
            showGroupedColumn: false,
        };
    }

    private _onRowDataBound = (args: RowDataBoundEventArgs) => {
        if (!args.row || !args.data) return;

        // Apply custom row class
        if (this.props.applyRowClass) {
            const className = this.props.applyRowClass(args.data);
            if (className) {
                (args.row as HTMLElement).classList.add(className);
            }
        }

        // Handle clickable links and URL columns
        let hasUrlColumn = false;
        for (let iCol = 0; iCol < this.props.columns.length; iCol++) {
            if (this.props.columns[iCol].isUrlValue) {
                hasUrlColumn = true;
                break;
            }
        }

        if (this.props.clickableLinks || hasUrlColumn) {
            const cells = (args.row as HTMLElement).querySelectorAll('td');
            cells.forEach((cell, index) => {
                const text = cell.textContent || '';
                let hasValidUrlValue = false;
                if (this.props.clickableLinks) {
                    hasValidUrlValue = this._isUrlValid(text);
                } else {
                    hasValidUrlValue = !!(this.props.columns[index]?.isUrlValue && (args.data as any).IsUrl);
                }
                if (hasValidUrlValue) {
                    cell.innerHTML = `<div class='grid-link'><a href='${text}' target='_blank'>${cell.innerHTML}</a></div>`;
                }
            });
        }

        if (this.props.rowCommandsHandler) {
            this.props.rowCommandsHandler(args);
        }
    }

    private _onRowSelected = (args: RowSelectEventArgs) => {
        if (this.props.selectRow && args.data) {
            this.props.selectRow(null, args.data);
        }
    }

    private _onCommandClick = (args: any) => {
        if (this.props.commands && args.rowData) {
            const target = args.target as HTMLElement | undefined;
            const clickedButton = target?.closest('button');
            const commandCell = clickedButton?.closest('td');
            const commandButtons = commandCell ? Array.from(commandCell.querySelectorAll('button')) : [];
            const buttonIndex = clickedButton ? commandButtons.indexOf(clickedButton) : -1;
            const cmdIndex = buttonIndex >= 0 ? buttonIndex : (args.commandColumn?._index ?? 0);
            const cmd = this.props.commands.command[cmdIndex];
            if (cmd?.click) {
                cmd.click(null, args.rowData);
            }
        }
    }

    private _collapseAll = () => {
        this.gridRef.current?.groupModule?.collapseAll();
    }

    private _expandAll = () => {
        this.gridRef.current?.groupModule?.expandAll();
    }

    private _getServices(): any[] {
        const services: any[] = [Resize];
        if (this.props.sort) services.push(Sort);
        if (this.props.filter) services.push(Filter);
        if (this.props.pageable) services.push(Page);
        if (this.props.groupBy) services.push(Group);
        if (this.props.commands) services.push(CommandColumn);
        return services;
    }

    private _getMappedValueText(col: any, fieldValue: any): string {
        for (let i = 0; i < col.values.length; i++) {
            const mappedValue = col.values[i].value;
            if (fieldValue === mappedValue || String(fieldValue).toLowerCase() === String(mappedValue).toLowerCase()) {
                return col.values[i].text;
            }
        }
        return '';
    }

    componentDidMount() {
        this.setState({ rendered: true });

        // Auto-select single row if applicable
        if (this.props.autoSelectSingleRow && this.props.dataSource?.length === 1) {
            setTimeout(() => {
                this.gridRef.current?.selectRow(0);
            }, 0);
        }

        // Hide header if requested
        if (this.props.hideHeader) {
            const gridElement = this.gridRef.current?.element;
            if (gridElement) {
                const header = gridElement.querySelector('.e-gridheader') as HTMLElement;
                if (header) header.style.display = 'none';
            }
        }
    }

    componentDidUpdate(prevProps: GridProps) {
        const grid = this.gridRef.current;
        if (!grid) return;

        // Update data source if changed
        if (prevProps.dataSource !== this.props.dataSource) {
            if (this.props.selectRow) {
                this.props.selectRow(null, null);
            }
            grid.dataSource = this.props.dataSource ? JSON.parse(JSON.stringify(this.props.dataSource)) : [];

            if (this.props.autoSelectSingleRow && this.props.dataSource?.length === 1) {
                setTimeout(() => grid.selectRow(0), 0);
            }
        }

        // Handle column visibility changes
        if (prevProps.columns !== this.props.columns) {
            this.props.columns.forEach((column) => {
                if (column.hasOwnProperty('visiable')) {
                    if (column.visiable) {
                        grid.showColumns(column.headerText);
                    } else {
                        grid.hideColumns(column.headerText);
                    }
                }
            });
        }

        // Hide header if requested
        if (this.props.hideHeader) {
            const header = grid.element?.querySelector('.e-gridheader') as HTMLElement;
            if (header) header.style.display = 'none';
        }
    }

    render() {
        const columns = this._buildColumns();
        const dataSource = this.props.dataSource ? JSON.parse(JSON.stringify(this.props.dataSource)) : [];
        const sortSettings = this._getSortSettings();
        const filterSettings = this._getFilterSettings();
        const pageSettings = this._getPageSettings();
        const groupSettings = this._getGroupSettings();
        const services = this._getServices();

        return (
            <div className="inovx-grid-shell">
                {this.props.groupBy ?
                    <DisclosureWidget containerClass="grid-groupby-cmd" btnClass="grid-control" collapseFunct={this._collapseAll} expandFunct={this._expandAll} />
                    : null
                }
                <GridComponent
                    ref={this.gridRef}
                    dataSource={dataSource}
                    allowResizing={true}
                    allowTextWrap={true}
                    allowSorting={!!this.props.sort}
                    allowFiltering={!!this.props.filter}
                    allowPaging={!!this.props.pageable}
                    allowGrouping={!!this.props.groupBy}
                    allowSelection={!!this.props.selectRow}
                    sortSettings={sortSettings}
                    filterSettings={filterSettings}
                    pageSettings={pageSettings}
                    groupSettings={groupSettings}
                    height={this.props.allowScrolling ? (this.props.scrollHeight || 'auto') : undefined}
                    width='100%'
                    rowDataBound={this._onRowDataBound}
                    rowSelected={this.props.selectRow ? this._onRowSelected : undefined}
                    commandClick={this.props.commands ? this._onCommandClick : undefined}
                    locale={typeof InovxLoader !== 'undefined' ? InovxLoader.getSyncfusionLocale() : 'en-US'}
                >
                    <ColumnsDirective>
                        {columns.map((col: any, idx: number) => {
                            if (col._isCommandColumn) {
                                return (
                                    <ColumnDirective
                                        key={`cmd-${idx}`}
                                        headerText={col.headerText}
                                        width={col.width}
                                        commands={col._commands}
                                    />
                                );
                            }

                            const colProps: any = {};
                            if (col.headerText) colProps.headerText = col.headerText;
                            if (col.field) colProps.field = col.field;
                            if (col.width) colProps.width = col.width;
                            // Default to 'string' to prevent EJ2 auto-detection from
                            // reformatting pre-formatted values (e.g. dates formatted by stores)
                            colProps.type = col.type || 'string';
                            if (col.template) colProps.template = col.template;
                            if (col.values instanceof Array && col.values.length > 0) {
                                colProps.valueAccessor = (field: string, data: any) => this._getMappedValueText(col, data[field]);
                            }
                            if (col.encoded === false) colProps.disableHtmlEncode = true;
                            if (col.hasOwnProperty('visiable')) colProps.visible = col.visiable;

                            return (
                                <ColumnDirective
                                    key={col.field || `col-${idx}`}
                                    {...colProps}
                                />
                            );
                        })}
                    </ColumnsDirective>
                    <Inject services={services} />
                </GridComponent>
            </div>
        );
    }
}

export default Grid;
